/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GetAccount200ResponsePositionsInner,
  GetOrders200Response,
  SideResponse,
} from "../../codegen-api";
import { displayFromRaw18 } from "../../utils/displayDecimals";
import { apiNum } from "../../utils/math";

/**
 * The trader's own state drawn on the TradingView chart: the open position
 * (entry, size, unrealized PnL), its liquidation price, and every open order
 * (cancellable from the chart). NativeCandlesChart accepted `position` and
 * `orders` and drew neither.
 *
 * Returns a disposer that removes everything it drew, so the caller can redraw
 * on any change without leaking lines.
 */
export interface LineColors {
  up: string;
  down: string;
  warning: string;
  text: string;
  bg: string;
}

const num = (v: unknown) => {
  const n = apiNum(displayFromRaw18(v as any) as any);
  return n !== undefined && Number.isFinite(n) ? n : undefined;
};

/** The price an order rests at: its trigger for a stop, else its limit. */
export const orderLinePrice = (o: GetOrders200Response) =>
  num((o as any).trigger) || num(o.price);

export const drawTradingLines = (
  chart: any,
  opts: {
    position?: GetAccount200ResponsePositionsInner;
    orders?: GetOrders200Response[];
    colors: LineColors;
    priceDecimals: number;
    onCancelOrder?: (order: GetOrders200Response) => void;
  }
): (() => void) => {
  const drawn: { remove: () => void }[] = [];
  const { position, orders, colors, priceDecimals, onCancelOrder } = opts;
  const fmt = (n: number) => n.toFixed(priceDecimals);

  const entry = num(position?.avg_entry_price);
  const size = num(position?.amount);
  if (position && entry && size) {
    const long = position.side === SideResponse.Buy;
    const color = long ? colors.up : colors.down;
    const pnl = num(position.unrealized_pnl);
    drawn.push(
      chart
        .createPositionLine()
        .setPrice(entry)
        .setQuantity(String(size))
        .setText(`${long ? "Long" : "Short"}${pnl !== undefined ? `  PnL ${pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}` : ""}`)
        .setTooltip(`Entry ${fmt(entry)}`)
        .setLineColor(color)
        .setBodyBorderColor(color)
        .setBodyTextColor(color)
        .setBodyBackgroundColor(colors.bg)
        .setQuantityBackgroundColor(color)
        .setQuantityBorderColor(color)
        .setExtendLeft(true)
    );

    const liq = num(position.liquidation_price);
    if (liq && liq > 0) {
      drawn.push(
        chart
          .createOrderLine()
          .setPrice(liq)
          .setText("Liquidation")
          .setQuantity("")
          .setTooltip(`Liquidation ${fmt(liq)}`)
          .setEditable(false)
          .setCancellable(false)
          .setLineStyle(2)
          .setLineColor(colors.warning)
          .setBodyBorderColor(colors.warning)
          .setBodyTextColor(colors.warning)
          .setBodyBackgroundColor(colors.bg)
          .setQuantityBackgroundColor(colors.warning)
          .setQuantityBorderColor(colors.warning)
      );
    }
  }

  (orders ?? []).forEach((o) => {
    const price = orderLinePrice(o);
    const qty = num(o.amount);
    if (!price || !qty) return;
    const buy = o.side === SideResponse.Buy;
    const color = buy ? colors.up : colors.down;
    const line = chart
      .createOrderLine()
      .setPrice(price)
      .setQuantity(String(qty))
      .setText(`${buy ? "Buy" : "Sell"} ${String(o.order_type ?? "").toLowerCase()}${o.reduce_only ? " (reduce)" : ""}`)
      .setTooltip(`${buy ? "Buy" : "Sell"} ${qty} @ ${fmt(price)}`)
      .setEditable(false)
      .setLineStyle(1)
      .setLineColor(color)
      .setBodyBorderColor(color)
      .setBodyTextColor(color)
      .setBodyBackgroundColor(colors.bg)
      .setQuantityBackgroundColor(color)
      .setQuantityBorderColor(color)
      .setCancelButtonBorderColor(color)
      .setCancelButtonIconColor(color)
      .setCancelButtonBackgroundColor(colors.bg);
    if (onCancelOrder) {
      line.setCancellable(true).onCancel(() => onCancelOrder(o));
    } else {
      line.setCancellable(false);
    }
    drawn.push(line);
  });

  return () => {
    drawn.forEach((d) => {
      try {
        d.remove();
      } catch {
        /* chart already gone */
      }
    });
  };
};
